import {Box, Text} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import {addDays} from 'date-fns';
import {parseCookies, setCookie} from 'nookies';
import type {FC} from 'react';
import {FaExternalLinkAlt} from 'react-icons/fa';
import {routes} from '../../../../config/constants';
import type {UserType} from '../../../../core/usecases/auth/AuthContext';
import {ENTRY_MANAGEMENT_FEATURE_NAME} from '../../../../features/entryManagements/constants/metaData';
import {cn} from '../../../../lib/classname';
import {CommonLink} from '../../CommonLink';
import {NotificationBadge} from './NotificationBadge';
import {ReportMenu} from './ReportMenu';

type MenuItemProps = {
  item: {
    name: string;
    url: string;
    target?: string;
    isNew?: boolean;
    isUpdate?: boolean;
    isEngineer?: boolean;
  };
  user?: UserType;
  handleMouseEnter: (item: {
    name: string;
    url: string;
    target?: string;
  }) => void;
  handleMouseLeave: (item: {
    name: string;
    url: string;
    target?: string;
  }) => void;
  shownReportMenu: boolean;
  isActive?: boolean;
  scoutsCount?: number;
  entryManagementRemindersCount?: number;
  todayDeadlineRecruitmentCount?: number;
};

export const MenuItem: FC<MenuItemProps> = ({
  item,
  user,
  handleMouseEnter,
  handleMouseLeave,
  shownReportMenu,
  isActive = false,
  scoutsCount = 0,
  entryManagementRemindersCount = 0,
  todayDeadlineRecruitmentCount = 0,
}) => {
  // スカウトの吹き出しは閉じると1日間非表示
  const [openedScoutPopup, {close: closeScoutPopup}] = useDisclosure(true, {
    onClose() {
      setCookie(null, 'hide_scout_notification', '1', {
        path: '/',
        expires: addDays(new Date(), 1),
      });
    },
  });
  // 募集を探すの吹き出しは閉じると1日間非表示
  const [
    openedRecruitmentDeadlinePopup,
    {close: closeRecruitmentDeadlinePopup},
  ] = useDisclosure(true, {
    onClose() {
      setCookie(null, 'hide_recruitment_deadline_notification', '1', {
        path: '/',
        expires: addDays(new Date(), 1),
      });
    },
  });
  const cookies = parseCookies();
  const isScoutPopupClosed =
    'hide_scout_notification' in cookies &&
    cookies.hide_scout_notification === '1';
  const isRecruitmentDeadlinePopupClosed =
    'hide_recruitment_deadline_notification' in cookies &&
    cookies.hide_recruitment_deadline_notification === '1';

  // 共通のリンクコンテンツ
  const linkContent = (
    <CommonLink href={item.url} target={item.target}>
      <span
        className={cn('font-bold', {
          'bg-background-2 rounded-12 px-16 py-4': isActive,
        })}
      >
        {item.name}
      </span>
      {item.target && (
        <span className="inline-flex">
          <FaExternalLinkAlt />
        </span>
      )}
    </CommonLink>
  );

  // 共通のバッジ表示
  const badges = (
    <>
      {item.isNew && (
        <Box className="absolute text-accent-1 top-[-16px] left-0 right-0 mx-auto text-xs">
          <Text className="text-center">New</Text>
        </Box>
      )}
      {item.isUpdate && (
        <Box className="absolute text-accent-1 top-[-16px] left-0 right-0 mx-auto text-xs">
          <Text className="text-center">Update</Text>
        </Box>
      )}
      {item.isEngineer && (
        <Box className="absolute text-engineer top-[-16px] left-0 right-0 mx-auto text-xs">
          <Text className="text-center whitespace-nowrap">For Engineer</Text>
        </Box>
      )}
    </>
  );

  // 通知バッジ設定を取得
  const getNotificationConfig = () => {
    if (item.name === 'スカウト') {
      return {
        count: scoutsCount,
        opened: openedScoutPopup && !isScoutPopupClosed,
        message: `${scoutsCount}件のスカウトがあります`,
        onClose: closeScoutPopup,
      };
    }

    if (item.name === ENTRY_MANAGEMENT_FEATURE_NAME) {
      return {
        count: entryManagementRemindersCount,
        opened: false,
        onClose: () => {},
      };
    }

    if (item.name === '募集を探す') {
      return {
        count: todayDeadlineRecruitmentCount,
        opened:
          openedRecruitmentDeadlinePopup && !isRecruitmentDeadlinePopupClosed,
        message: `${todayDeadlineRecruitmentCount}件の本日締切の募集があります`,
        onClose: closeRecruitmentDeadlinePopup,
      };
    }

    return null;
  };

  // 共通コンテンツ
  const content = (
    <>
      {linkContent}
      {badges}
    </>
  );

  const notificationConfig = getNotificationConfig();

  return (
    <div
      onMouseEnter={() => {
        handleMouseEnter(item);
      }}
      onMouseLeave={() => {
        handleMouseLeave(item);
      }}
      className="relative"
    >
      {notificationConfig ? (
        <NotificationBadge {...notificationConfig}>{content}</NotificationBadge>
      ) : (
        content
      )}
      {shownReportMenu && item.url === routes.REPORTS && (
        <div
          className={`absolute ${user && 'top-[10px]'} w-[100px] z-50 ml-[-12px] pt-12`}
        >
          <div className="overflow-hidden rounded-12 bg-background-1 shadow-modal">
            <ReportMenu />
          </div>
        </div>
      )}
    </div>
  );
};
